import React, { Component, useState, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from 'react-router-dom';
import 'react-notifications/lib/notifications.css';
import { NotificationContainer } from 'react-notifications';
import { Provider, Subscribe } from 'unstated';
import 'nprogress/nprogress.css';
import appState, { AppStateContainer } from './state/AppStateContainer';
import Loading from './components/shared/Loading';
import PageBidRequestList from './ts-components/page-bid-request-list/page-bid-request-list';
import PageMarketing from './ts-components/page-marketing/page-marketing';
import PageDashboardV2 from './ts-components/page-dashboard-V2/page-dashboard-v2';
import PageHome from './ts-components/page-home/page-home';
import PageProjects from './ts-components/page-projects/page-projects';
import PageQuotation from './ts-components/page-quotation/page-quotation';
import PageFiles from './ts-components/page-files/page-files';
import PageSupplierPresentation from './ts-components/page-supplier-presentation/page-supplier-presentation';
import PageReports from './ts-components/page-reports/page-reports';
import RatingForm from './ts-components/rating-form/rating-form.jsx';
import InternetError from './ts-components/page-response-message/page-response-message-error.jsx';
import BrowserSupport from './ts-components/page-browser-support/page-browser-support.jsx';
import _ from 'lodash';
import PageLogin from './ts-components/page-login/page-login';
import PageChangePassword from './ts-components/page-password/page-change-password';
import PageSso from './ts-components/page-sso/page-sso';
import UserInvitation from './ts-components/page-registration/user-invitation';
import UserInvitationError from './ts-components/page-registration/user-invitation-error';
import Signup from './ts-components/page-registration/page-signup';
import { ServiceAuth } from './services/service-auth';
import appEnum, { PrequalificationDecision } from './util/appEnum';
import { installInterceptors } from './services/axiosHelper';
import PageHelp from './ts-components/page-help/page-help';
import SupplierSignup from './ts-components/page-supplier-signup/page-supplier-signup';
import { ServiceSupplier } from './services/service-supplier';
import PageUpdates from './ts-components/page-updates/page-updates';
import PageSupplierSignupWizard from './ts-components/page-supplier-signup-wizard/page-supplier-signup-wizard';
import PageTerms from './ts-components/page-terms/page-terms';
import { PageContractStatus } from './ts-components/page-contract-status/page-contract-status';
import PageGetStarted from './ts-components/page-get-started/page-get-started';
import { PageSupplierMonitoring } from './ts-components/page-supplier-monitoring/page-supplier-monitoring';
import PagePublicBidRequestList from './ts-components/page-public-bid-request/page-public-bid-request-list';
import PageInvoices from './ts-components/page-invoices/page-invoices';
import PageEmailDebug from './ts-components/page-email-debug/page-email-debug';
import PageApiClients from './ts-components/page-api-clients/page-api-clients';
import PageSalesTool from './ts-components/page-sales-tool/page-sales-tool';
import PagePublicBidRequest from './ts-components/page-public-bid-request/page-public-bid-request';
import PageHvdLogin from './ts-components/page-hvd-login/PageHvdLogin';
import PageDeadlinesMessages from './ts-components/page-deadlines-messages/page-deadlines-messages';
import serviceSales from './services/service-sales';
import { ServiceUser } from './services/service-user';
import { darkenColor } from './util';
import { PagePublishedBidRequestsList } from './ts-components/page-public-bid-request/page-published-bid-requests-list/page-published-bid-requests-list';
import { serviceOrganization } from './services/service-organization';
import { ChristmasSpirit } from './components/shared/ChristmasSpirit/ChristmasSpirit';
import { PageWeeklyDigestUnsubscribe, PageSupplierInsuranceReminderUnsubscribe } from './ts-components/page-email-unsubscribe/page-email-unsubscribe';

installInterceptors();

function orgRoute({ component, path, exact, render, orgSlug = "" }) {
  const result = [<Route component={component} path={`${orgSlug}${path}`} exact={exact} render={render} key="route" />];
  if (orgSlug && orgSlug !== "/") {
    result.push(<Redirect path={path} exact={exact} to={`${orgSlug}${window.location.pathname}`} key="redirect" />);
  }
  return result;
}
function protectedOrgRoute({ component, render, path, exact, notAllowedRoles = [], product, orgSlug = "" }) {
  const result = [<ProtectedRoute component={component} render={render} path={`${orgSlug}${path}`} exact={exact} notAllowedRoles={notAllowedRoles} product={product} key="route" />];
  if (orgSlug && orgSlug !== "/") {
    result.push(<Redirect path={path} exact={exact} to={`${orgSlug}${window.location.pathname}`} key="redirect" />);
  }
  return result;
}

function ProtectedRoute({ component, render, path, exact, notAllowedRoles = [], product }) {
  if (appState.state.token && !_.isNull(appState.state.token)) {
    if (
      !notAllowedRoles.includes(appState.state.token.role) &&
      !(product && !(appState.getActiveProducts().includes(product))) ||
      appState.isSuperAdminOrg()
    ) {
      return <Route path={path} component={component} render={render} exact={exact} />;
    } else {
      return <Redirect to={path.replace(path.split("/")[2], "dashboard")} />;
    }
  } else {
    if (window.location.pathname !== '/' && !window.location.pathname.includes('/login')) {
      appState.persistString("urlToRedirect", window.location.pathname);
    }
    return <Route component={PageHome} />;
  }
}

class App extends Component {
  state = {
    appStateLoaded: false,
    orgSlugState: {},
    supplierProfileHasBeenUpdated: false,
  };
  orgSlug;
  async componentDidMount() {
    const [, orgSlug, url] = window.location.pathname.split("/");
    this.detectBrowser();
    if (url === 'invite') {
      appState.setState({ loader: true });
      this.orgSlug = `/${orgSlug}`;
      this.setState({ orgSlugState: { slug: this.orgSlug, wasValid: true }, appStateLoaded: true });
      appState.setState({ loader: false });
    } else if (orgSlug === 'sso' || orgSlug === 'hvdlogin') {
      appState.setState({ loader: true, loaded: true });
      this.setState({ appStateLoaded: true });
    } else if (orgSlug === 'companyId') {
      appState.setState({ loader: true });
      try {
        const orgSlug = await serviceOrganization.getOrgSlugWithOnePlatformCompanyId(url);
        this.orgSlug = `/${orgSlug}`;
        this.setState({ orgSlugState: { slug: this.orgSlug, wasValid: true }, appStateLoaded: true });
        appState.setState({ loader: false });
        window.location.href = window.location.href.replace(`companyId/${url}`, orgSlug);
      } catch (e) {
        appState.setState({ loader: false });
        const lastRoute = window.location.href.split('/').pop().replace(/\?.*/, '');
        if (lastRoute === 'published-requests-table') {
          window.location.href = window.location.href.replace(/companyId\/[^?]*/, 'published-quotations');
        } else {
          window.location.href = window.location.href.replace(/companyId\/[^?]*/, '404');
        }
      }
    } else {
      appState.setState({ loader: true });
      const root_theme = document.querySelector(':root');
      const theme = appState.getTheme();
      if (theme) {
        const {
          backgroundUrl,
          logoUrl,
          themeValues,
        } = theme;

        root_theme.style.setProperty('--primary-button-background-color', themeValues['theme-primary-background']);
        root_theme.style.setProperty('--primary-button-background-color-hover', darkenColor('var(--primary-button-background-color)', '90%'));
        root_theme.style.setProperty('--primary-button-text', themeValues['theme-primary-text']);
        root_theme.style.setProperty('--secondary-button-background-color', themeValues['theme-secondary-background'] ?? 'white');
        root_theme.style.setProperty('--secondary-button-background-color-hover', darkenColor('var(--secondary-button-background-color)', '97%'));
        root_theme.style.setProperty('--secondary-button-text', themeValues['theme-secondary-text']);
        root_theme.style.setProperty('--menu-text-color', themeValues['theme-background-text']);
        root_theme.style.setProperty('--menu-text-color-not-focused', darkenColor('var(--menu-text-color)', '90%'));
        root_theme.style.setProperty('--background-image', `url(${backgroundUrl})`);
        root_theme.style.setProperty('--menu-background-color', themeValues['theme-background-splash']);
        root_theme.style.setProperty('--primaryFont', themeValues['theme-font']);
        appState.setLogoUrl(logoUrl);
      }
      await this.checkSupplierSession();
      await this.handleInitialMounting();
      if (appState.isAdminRole() && appState.isPurchasingActive()) {
        // Set initial supplier application count, will be updated when suppliers are fetched in supplier database
        const supplierApplications = await ServiceSupplier.fetchSupplier({ mini: true, prequalification: PrequalificationDecision.INBOX });
        appState.setSupplierApplicationCount(supplierApplications.inboxCount);
      }
    }

    window.addEventListener("storage", this.onStorageUpdate);
  }

  checkSupplierSession = async () => {
    try {
      const orgSlug = appState.getOrgSlugFromUrl();
      let supplierSpecificRoute = appState.isSupplierSpecificRoute();
      if (supplierSpecificRoute) {
        let encryptedLink = window.location.pathname.split('/').find(s => s.length >= 128);
        if (encryptedLink) await ServiceAuth.verifyAndGetSupplierToken(encryptedLink, orgSlug);
      }
    } catch (error) {
      console.log(error?.response)
    }
  };

  handleInitialMounting = async () => {
    const { slug, wasValid } = await appState.getCurrentOrgUrl();
    if (slug) {
      this.orgSlug = slug;
    }
    const user = await appState.getUserDetails(); //Checks for updated details on reload
    if (appState.isSalesActive() && !appState.isSupplierSpecificRoute()) {
      const supplierProfileHasBeenUpdated = await serviceSales.hasProfileBeenUpdated();
      appState.setSupplierProfileHasBeenUpdated(supplierProfileHasBeenUpdated);

      try {
        const users = await ServiceUser.getTasksUsers();
        const teamComplete = users.length >= 2;
        appState.setTeamComplete(teamComplete);
      } catch (error) {
        console.error('Error checking team completion:', error);
        appState.setTeamComplete(false);
      }
    }
    this.setState({ orgSlugState: { slug, wasValid }, appStateLoaded: true });
    appState.setState({ loader: false });
  }

  onStorageUpdate = ({ newValue, key }) => {
    if (key === "token") {
      let newVal = newValue && newValue !== 'undefined' && newValue !== 'null' ? JSON.parse(newValue) : "";
      if (appState.state.token?.id !== newVal?.id) {
        appState.setState({ token: null });
      }
    }
  };

  componentWillUnmount() {
    window.removeEventListener("storage", this.onStorageUpdate);
  }

  detectBrowser() {
    const ua = navigator.userAgent;
    const is_ie = ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    this.setState({
      is_ie: is_ie,
    });
  }

  renderLoader() {
    return (
      <>
        <div
          style={
            appState.state.loader
              ? { pointerEvents: 'none', zIndex: '9999', width: '100%', height: '100%' }
              : {}
          }
        >
          <div className={`app-loader ${appState.state.loader ? ' loader-show' : ''}`}>
            <span className="bounce1"></span>
            <span className="bounce2"></span>
            <span className="bounce3"></span>
          </div>
        </div>
        <NotificationContainer />
      </>
    );
  }

  _renderApp(appState) {
    if (appState.state.loaded && this.state.appStateLoaded) {
      const startPage = (() => {
        if (appState.isPurchasingActive()) return PageDashboardV2;

        if (appState.isSalesActive()) {
          if (appState.hasSupplierProfileBeenUpdated()) {
            return () => <Redirect to={`${this.orgSlug}/requests`} />;
          } else {
            return () => <Redirect to={`${this.orgSlug}/get-started`} />;
          }
        }
      })();
      const orgSlug = this.orgSlug;

      return (
        <Router>
          <div>
            <section className="body-wrapper" id="content">
              {this.renderLoader()}
              <ChristmasSpirit />
              <Suspense fallback="">
                <Switch>
                  {['localhost:3000', 'test.accurator.se', 'app.accurator.se'].includes(window.location.host) && (
                    <Route path="/supplier-sign-up" component={PageSupplierSignupWizard} />
                  )}
                  {['localhost:3000', 'test.accurator.se', 'app.accurator.se'].includes(window.location.host) && (
                    <Route path={`/contract-status/:code`} exact={true} component={PageContractStatus} />
                  )}
                  <Route path="/terms" exact component={PageTerms} />
                  <Route path={`/error`} exact={true} render={() => <InternetError />} />
                  <Route path={`/invite/error`} exact={true} render={() => <UserInvitationError />} />
                  <Route path={`/login`} render={() => <PageLogin />} />
                  <Route path={`/sso`} render={() => <PageSso />} />
                  <Route path={`/hvdlogin`} render={() => <PageHvdLogin />} />
                  <Route path={`/published-quotations`} render={() => <PagePublishedBidRequestsList onlyPublic />} />

                  <Route
                    path={`/forgot/password/:verificationcode?`}
                    component={PageChangePassword}
                  />

                  <Route
                    path={`/weekly-digest/unsubscribe/:p/:s`}
                    component={PageWeeklyDigestUnsubscribe}
                  />

                  <Route
                    path={`/supplier-insurance-reminder/unsubscribe/:p/:s`}
                    component={PageSupplierInsuranceReminderUnsubscribe}
                  />

                  <Route
                    path={`/browser-support`}
                    exact={true}
                    render={() => <BrowserSupport />}
                  />

                  {(this.orgSlug && appState.state.token && !_.isNull(appState.state.token)) && ['/', this.orgSlug, `${this.orgSlug}/`, `/${this.orgSlug}`, `/${this.orgSlug}/`].map((path, index) => {
                    return (
                      <Route
                        path={path}
                        key={index}
                        exact={true}
                        render={() => (
                          <Redirect to={`${this.orgSlug}/dashboard`} />
                        )}
                      />
                    )
                  })}
                  {protectedOrgRoute({ orgSlug, path: `/dashboard`, exact: true, component: startPage })}
                  {protectedOrgRoute({ orgSlug, path: `/todo`, exact: true, component: PageDeadlinesMessages, product: "PURCHASING" })}

                  {/* Superadmin routes */}
                  {protectedOrgRoute({ orgSlug, path: `/organizations`, component: PageProjects })}
                  {protectedOrgRoute({ orgSlug, path: `/reports`, component: PageReports })}
                  {protectedOrgRoute({ orgSlug, path: `/invoices`, component: PageInvoices })}
                  {protectedOrgRoute({ orgSlug, path: `/api-clients`, component: PageApiClients })}
                  {protectedOrgRoute({ orgSlug, path: `/email-debug`, component: PageEmailDebug })}
                  {protectedOrgRoute({ orgSlug, path: `/sales-tool`, component: PageSalesTool })}


                  {orgRoute({ orgSlug, path: `/quotation/info/:id`, render: () => <PageQuotation orgSlug={this.orgSlug.replace('/', '')} /> })}
                  {orgRoute({ orgSlug, path: `/conversation`, render: () => this.renderLoader() })}
                  {orgRoute({ orgSlug, path: `/rating`, render: () => <RatingForm /> })}

                  {/* Purchasing routes */}
                  {protectedOrgRoute({ orgSlug, path: `/projects`, component: PageProjects, product: "PURCHASING" })}
                  {protectedOrgRoute({ orgSlug, path: `/tasks`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client], product: "PURCHASING" })}
                  {protectedOrgRoute({ orgSlug, path: `/supplier-database`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client], product: "PURCHASING" })}
                  {protectedOrgRoute({ orgSlug, path: `/categories`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client], product: "PURCHASING" })}
                  {protectedOrgRoute({ orgSlug, path: `/supplier-monitoring`, component: PageSupplierMonitoring, product: "PURCHASING" })}
                  {protectedOrgRoute({ orgSlug, path: `/files`, component: PageFiles, product: "PURCHASING" })}

                  {/* Sales routes */}
                  {protectedOrgRoute({ orgSlug, path: `/published-requests-table`, render: () => <PagePublishedBidRequestsList openToNewTab />, product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/requests`, component: PageBidRequestList, product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/published-requests/:orgId/:projectId/:areaId`, exact: true, component: PagePublicBidRequest, product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/published-requests`, component: PagePublicBidRequestList, product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/supplier-presentation`, component: PageSupplierPresentation, notAllowedRoles: [appEnum.Role.Client], product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/marketing`, component: PageMarketing, product: "SALES" })}
                  {protectedOrgRoute({ orgSlug, path: `/get-started`, component: PageGetStarted, product: "SALES" })}

                  {/* Common routes */}
                  {protectedOrgRoute({ orgSlug, path: `/chat`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client] })}
                  {protectedOrgRoute({ orgSlug, path: `/area`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client] })}
                  {protectedOrgRoute({ orgSlug, path: `/settings`, component: PageProjects, notAllowedRoles: [appEnum.Role.Client, appEnum.Role.Inköp, appEnum.Role.Kalkyl] })}

                  {orgRoute({ orgSlug, path: `/forgot/password/:verificationcode?`, component: PageChangePassword })}
                  {orgRoute({ orgSlug, path: `/change/password/`, component: PageChangePassword })}
                  {orgRoute({ orgSlug, path: `/syna/report`, render: () => window.fetchingReport ? <Loading message="Hämtar rapport..." /> : null })}
                  {orgRoute({ orgSlug, path: `/projects/:project/:area/tips`, component: PageProjects })}
                  {orgRoute({ orgSlug, path: `/invite/:code?`, render: props => <UserInvitation {...props} /> })}
                  {orgRoute({ orgSlug, path: `/signup`, render: props => <Signup {...props} /> })}
                  {orgRoute({ orgSlug, path: `/new-supplier`, component: SupplierSignup })}
                  {orgRoute({ orgSlug, path: `/profile`, render: props => <PageProjects {...props} /> })}
                  {orgRoute({ orgSlug, path: `/help`, render: () => <PageHelp /> })}
                  {orgRoute({ orgSlug, path: `/updates`, render: () => <PageUpdates /> })}

                  {this.state.is_ie && <Route path={path} exact={true} render={() => <Redirect to="/browser-support" />} />}
                  {(!appState.state.token || _.isNull(appState.state.token)) && !appState.isSupplierSpecificRoute() && <Route render={() => <PageHome />} />}

                  <Route render={() => <InternetError />} />
                </Switch>
              </Suspense>
            </section>
          </div>
        </Router>
      );
    } else {
      return (
        <div>
          <section className="body-wrapper" id="content">
            {this.renderLoader()}
          </section>
        </div>
      );
    }
  }

  render() {
    return (
      <Subscribe to={[AppStateContainer]}>
        {appState => this._renderApp(appState)}
      </Subscribe>
    );
  }
}

const WrappedApp = () => {
  return (
    <Provider inject={[appState]}>
      <App />
    </Provider>
  );
};

export default WrappedApp;
